import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useParams } from "react-router-dom";
const headerImg = "/header.png";

function pick(obj, keys, fallback = "") {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
}

function ymd(val) {
  const s = String(val || "");
  return s ? s.slice(0, 10) : "";
}

function isNumLike(v) {
  const n = Number(v);
  return Number.isFinite(n);
}

function detectSourceId(r) {
  const direct = pick(r, [
    "sourceHeaderId",
    "source_header_id",
    "sourceId",
    "source_id",
    "crush1HeaderId",
    "crush1_header_id",
    "crush1Id",
    "crush1_id",
    "sourceCrush1Id",
    "source_crush1_id",
    "sourceEntryId",
    "source_entry_id",
  ]);
  if (direct && isNumLike(direct)) return Number(direct);

  const nestedObj =
    r?.source ||
    r?.sourceRef ||
    r?.source_ref ||
    r?.crush1Source ||
    r?.crush1_source ||
    null;

  if (nestedObj) {
    const nested = pick(nestedObj, [
      "headerId",
      "header_id",
      "id",
      "sourceHeaderId",
      "source_header_id",
    ]);
    if (nested && isNumLike(nested)) return Number(nested);
  }

  for (const [k, v] of Object.entries(r || {})) {
    const key = String(k).toLowerCase();
    const looksLikeSource =
      (key.includes("source") || key.includes("crush1")) &&
      (key.includes("id") || key.includes("header"));
    if (looksLikeSource && isNumLike(v)) return Number(v);
  }

  return null;
}

function formatSource(r) {
  const ready = pick(r, [
    "sourceDisplay",
    "source_display",
    "sourceText",
    "source_text",
    "sourceLabel",
    "source_label",
  ]);
  if (ready) return ready;

  const srcId = detectSourceId(r);
  if (!srcId) return "-";

  const itemCode = pick(r, ["itemCode", "item_code"], "");
  const remRaw = pick(
    r,
    ["remainingKg", "remaining_kg", "remKg", "rem_kg"],
    ""
  );
  const rem = isNumLike(remRaw) ? Number(remRaw) : null;

  if (itemCode && rem !== null) {
    return `#${srcId} | ${itemCode} | Rem: ${rem.toFixed(3)} KG`;
  }
  if (itemCode) return `#${srcId} | ${itemCode}`;
  return `#${srcId}`;
}

export default function Crush2Print() {
  const { id } = useParams();

  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [readyToPrint, setReadyToPrint] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setReadyToPrint(false);

        const headersRes = await api.get(`/api/crush/crush2/headers`);
        const one = (headersRes.data || []).find(
          (x) => String(x.headerId) === String(id)
        );

        const linesRes = await api.get(`/api/crush/crush2/headers/${id}/lines`);

        if (!alive) return;

        setHeader(one || null);
        setLines(Array.isArray(linesRes.data) ? linesRes.data : []);
        setReadyToPrint(true);
      } catch (e) {
        alert(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e.message ||
            "Print load failed"
        );
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    if (!readyToPrint || !header) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  }, [readyToPrint, header]);

  const totalKg = useMemo(() => {
    return (lines || []).reduce((s, r) => {
      const n = Number(pick(r, ["resultKg", "result_kg"], 0));
      return s + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [lines]);

  if (!header) return <div style={{ padding: 20 }}>Loading...</div>;

  return (
    <>
      <style>{`
        @page {
          size: A4 landscape;
          margin: 10mm;
        }

        html, body {
          margin: 0;
          padding: 0;
          background: #fff;
          font-family: Arial, sans-serif;
          color: #000;
        }

        .print-page {
          width: 277mm;
          margin: 0 auto;
          padding-bottom: 18mm;
          box-sizing: border-box;
          position: relative;
          background: #fff;
        }

        .company-header {
          width: 277mm;
          height: 32mm;
          margin: 0 auto 6px auto;
          position: relative;
          text-align: center;
          overflow: hidden;
        }

        .company-header-img {
          width: 277mm;
          height: 32mm;
          object-fit: contain;
          display: block;
          margin: 0 auto;
        }

        .report-title {
          text-align: center;
          font-size: 17px;
          font-weight: 700;
          margin: 4px 0 8px 0;
        }

        .close-btn {
          position: absolute;
          right: 0;
          top: 0;
          background: #dc3545;
          color: #fff;
          border: none;
          padding: 6px 12px;
          font-size: 12px;
          border-radius: 4px;
          cursor: pointer;
        }

        .info-box {
          width: 277mm;
          display: flex;
          justify-content: space-between;
          border: 1px solid #000;
          margin: 0 auto 8px auto;
          box-sizing: border-box;
        }

        .info-box > div {
          flex: 1;
          padding: 7px 8px;
          box-sizing: border-box;
        }

        .lbl {
          font-size: 10px;
          margin-bottom: 5px;
        }

        .val {
          font-size: 13px;
          font-weight: 700;
        }

        .print-table {
          width: 277mm;
          margin: 0 auto;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .print-table th,
        .print-table td {
          border: 1px solid #000;
          padding: 5px 4px;
          font-size: 11px;
          vertical-align: top;
          text-align: left;
          box-sizing: border-box;
        }

        .print-table th {
          font-weight: 700;
        }

        .bold {
          font-weight: 700;
          text-align: right;
        }

        .wrap {
          white-space: normal;
          word-break: break-word;
          line-height: 1.35;
        }

        .print-footer {
          position: fixed;
          bottom: 8mm;
          left: 10mm;
          right: 10mm;
          width: auto;
          border-top: 1px solid #000;
          padding-top: 8px;
          display: flex;
          justify-content: space-between;
          box-sizing: border-box;
          background: #fff;
        }

        .print-footer > div {
          width: 45%;
        }

        .footer-label {
          font-size: 10px;
          font-weight: 600;
          margin-bottom: 6px;
        }

        .footer-value {
          font-size: 16px;
          font-weight: 700;
          margin-top: 6px;
        }

        .footer-right {
          text-align: right;
        }

        @media print {
          .no-print {
            display: none !important;
          }

          * {
            box-shadow: none !important;
            background-image: none !important;
          }
        }
      `}</style>

      <div className="print-page">
        <div className="company-header">
          <img
            src={headerImg}
            alt="Saudi Mais Header"
            className="company-header-img"
          />

          <button
            type="button"
            className="close-btn no-print"
            onClick={() => {
              window.close();
              setTimeout(() => {
                window.location.href = "/crush-2";
              }, 300);
            }}
          >
            ✕ Close
          </button>
        </div>

        <div className="report-title">
          DAILY CRUSH & RECYCLING DEPARTMENT PRODUCTION
        </div>

        <div className="info-box">
          <div>
            <div className="lbl">Entry ID</div>
            <div className="val">{header.headerId}</div>
          </div>

          <div>
            <div className="lbl">Date</div>
            <div className="val">{ymd(header.productionDate)}</div>
          </div>

          <div>
            <div className="lbl">Total KG</div>
            <div className="val">{Number(totalKg).toFixed(2)}</div>
          </div>
        </div>

        <table className="print-table">
          <thead>
            <tr>
              <th>Sr</th>
              <th>Source (Crush 1)</th>
              <th>Machine No</th>
              <th>Operator</th>
              <th>Time/Hour</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Remaining KG</th>
              <th>Result KG</th>
              <th>Receive Date</th>
            </tr>
          </thead>

          <tbody>
            {lines.map((r, i) => {
              const machineNo = pick(r, ["machineNo", "machine_no"], "-");
              const operator = pick(r, ["operatorName", "operator_name"], "-");
              const timeHour = pick(r, ["timeHour", "time_hour"], "-");
              const itemCode = pick(r, ["itemCode", "item_code"], "-");
              const itemName = pick(r, ["itemName", "item_name"], "-");

              const remaining = Number(
                pick(r, ["remainingKg", "remaining_kg", "remKg", "rem_kg"], 0)
              );
              const result = Number(pick(r, ["resultKg", "result_kg"], 0));
              const rcvDate = ymd(pick(r, ["receiveDate", "receive_date"], ""));

              return (
                <tr key={r.lineId ?? i}>
                  <td>{i + 1}</td>
                  <td className="wrap">{formatSource(r)}</td>
                  <td>{machineNo}</td>
                  <td>{operator}</td>
                  <td>{timeHour}</td>
                  <td>{itemCode}</td>
                  <td className="wrap">{itemName}</td>
                  <td className="bold">
                    {Number.isFinite(remaining) ? remaining.toFixed(2) : "0.00"}
                  </td>
                  <td className="bold">
                    {Number.isFinite(result) ? result.toFixed(2) : "0.00"}
                  </td>
                  <td>{rcvDate || "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="print-footer">
          <div>
            <div className="footer-label">Production Manager</div>
            <div className="footer-value">{header.checkedBy || ""}</div>
          </div>

          <div className="footer-right">
            <div className="footer-label">Recycling In Charge</div>
            <div className="footer-value">{header.receivedBy || ""}</div>
          </div>
        </div>
      </div>
    </>
  );
}